import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    computed,
    inject,
    input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ScrollContainerService } from '@frontend/sports/common/core/utils/scroll-container';
import { ThemeOptions } from '@frontend/sports/common/ui/shared';
import { SportsThemedIconButtonDirective } from '@frontend/sports/common/ui/sports-themed-button';
import { GridSorting } from '@frontend/sports/grid/core/feature/model';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { DsButtonIcon } from '@frontend/ui/button-icon';
import { DsRadioModule } from '@frontend/ui/radio-button';
import { DsSegmentedControlModule } from '@frontend/ui/segmented-control';
import { WINDOW } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Subscription, filter, fromEvent, merge } from 'rxjs';

@Component({
    selector: 'ms-sort-selector',
    templateUrl: 'sort-selector.component.html',
    styleUrls: ['./sort-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DsSegmentedControlModule, DsRadioModule, DsButtonIcon, IconCustomComponent, SportsThemedIconButtonDirective],
})
export class SortSelectorComponent {
    @Input() sorting: GridSorting;
    @Input() sortingTracking?: string;
    @Input() showRadioInput?: boolean;
    @Input() eventDetails?: string;
    isInverseRequired = input(false);
    @Output() sortingChange = new EventEmitter<GridSorting>();
    @ViewChild('sortheader', { read: ElementRef, static: true }) sortHeader: ElementRef;
    readonly themeInverse = computed<ThemeOptions<boolean>>(() => {
        return this.isInverseRequired() ? { 'mgm-4': true, 'mgm-dice': true } : {};
    });
    gridSorting = GridSorting;
    collapsed: boolean;
    radioSelected: string = GridSorting.Competition.toString();
    private scrollSubscription: Subscription;
    private readonly destroyRef = inject(DestroyRef);
    private readonly _window = inject(WINDOW);

    constructor(
        public sitecore: Sitecore,
        private scrollContainer: ScrollContainerService,
        private changeRef: ChangeDetectorRef,
        private trackingService: TrackingService,
    ) {
        this.collapsed = true;
    }

    get sortByTime(): boolean {
        return this.sorting === GridSorting.Time;
    }

    toggleSelector(): void {
        if (!this.collapsed) {
            this.collapse();

            return;
        }

        this.collapsed = false;

        this.sendTracking('Sorting Entry Point', 'Click on Sort');

        const windowClick = fromEvent(this._window, 'click').pipe(filter((e) => !this.sortHeader.nativeElement.contains(e.target)));

        this.scrollSubscription = merge(this.scrollContainer.scroll, fromEvent(this._window, 'scroll'), windowClick)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.collapse();
            });
    }

    collapse(): void {
        if (this.scrollSubscription && !this.scrollSubscription.closed) {
            this.scrollSubscription.unsubscribe();
        }

        if (this.collapsed) {
            return;
        }

        this.collapsed = true;
        this.changeRef.markForCheck();
    }

    selectSortCriteria(criteria: string): void {
        this.collapse();
        this.radioSelected = criteria.toString();
        if (criteria === GridSorting.Time.toString()) {
            this.sendTracking('Sort_Time', 'Sort');
        } else if (criteria === GridSorting.Competition.toString()) {
            this.sendTracking('Sort_Competitions', 'Sort');
        }

        this.sortingChange.next(+criteria);
    }

    private sendTracking(labelEvent: string, actionEvent: string): void {
        const trackingData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Events Sorting',
            [trackingConstants.COMPONENT_LABEL_EVENT]: labelEvent,
            [trackingConstants.COMPONENT_ACTION_EVENT]: actionEvent,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: 'Sort_EntryPoint_' + this.sortingTracking,
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: this.eventDetails || NOT_APPLICABLE,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        };

        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingData);
    }
}
